<script>
import { defineComponent as _defineComponent } from "vue";
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, mergeProps as _mergeProps, createBlock as _createBlock, withCtx as _withCtx, TransitionGroup as _TransitionGroup, createVNode as _createVNode, createElementVNode as _createElementVNode, Teleport as _Teleport } from "vue";
const _hoisted_1 = ["data-id", "data-position", "data-expanded"];
import {
  toValue,
  ref,
  provide,
  watch,
  onBeforeUnmount
} from "vue";
import { onClickOutside } from "@vueuse/core";
import { useToastState } from "../composables/private/useToastState";
import { useToastView } from "../composables/private/useToastView";
import { useToastCallback } from "../composables/private/useToastCallback";
import MagicToastView from "./MagicToastView.vue";
import { MagicToastInstanceId } from "../../symbols";
import "@maas/vue-equipment/utils/css/animations/fade-out.css";
import "@maas/vue-equipment/utils/css/animations/squash-y.css";
import "@maas/vue-equipment/utils/css/animations/slide-ltr-in.css";
import "@maas/vue-equipment/utils/css/animations/slide-ltr-out.css";
import "@maas/vue-equipment/utils/css/animations/slide-rtl-in.css";
import "@maas/vue-equipment/utils/css/animations/slide-rtl-out.css";
import { useToastListener } from "../composables/private/useToastListener";
export default /* @__PURE__ */ _defineComponent({
  ...{
    inheritAttrs: false
  },
  __name: "MagicToastProvider",
  props: {
    id: { type: null, required: true },
    options: { type: Object, required: false }
  },
  setup(__props) {
    const { deleteView } = useToastView(__props.id);
    const { deleteState, initializeState } = useToastState(__props.id);
    const state = initializeState(__props.options);
    const listRef = ref(void 0);
    const {
      onBeforeEnter,
      onEnter,
      onAfterEnter,
      onBeforeLeave,
      onLeave,
      onAfterLeave
    } = useToastCallback(__props.id);
    const { onMouseenter, onMouseleave, outsideClickCallback } = useToastListener(__props.id);
    onClickOutside(listRef, outsideClickCallback);
    watch(
      () => __props.options,
      (value) => {
        initializeState(value);
      },
      {
        deep: true
      }
    );
    onBeforeUnmount(() => {
      deleteState();
    });
    provide(MagicToastInstanceId, __props.id);
    return (_ctx, _cache) => {
      return _openBlock(), _createBlock(_Teleport, {
        to: _unref(state).options.teleport?.target,
        disabled: _unref(state).options.teleport?.disabled
      }, [
        _createElementVNode("div", _mergeProps({
          "data-id": toValue(_ctx.id),
          "data-position": _unref(state).options.position,
          "data-expanded": _unref(state).expanded,
          class: "magic-toast-provider"
        }, _ctx.$attrs), [
          _createVNode(_TransitionGroup, {
            ref_key: "listRef",
            ref: listRef,
            tag: "ol",
            class: "magic-toast-provider__list",
            name: _unref(state).options.transition,
            "on-before-enter": _unref(onBeforeEnter),
            "on-enter": _unref(onEnter),
            "on-after-enter": _unref(onAfterEnter),
            "on-before-leave": _unref(onBeforeLeave),
            "on-leave": _unref(onLeave),
            "on-after-leave": _unref(onAfterLeave),
            onMouseenter: _unref(onMouseenter),
            onMouseleave: _unref(onMouseleave)
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(
                _Fragment,
                null,
                _renderList(_unref(state).views, (view, index) => {
                  return _openBlock(), _createBlock(MagicToastView, {
                    id: view.id,
                    key: view.id,
                    index
                  }, {
                    default: _withCtx(() => [
                      (_openBlock(), _createBlock(_resolveDynamicComponent(view.component), _mergeProps({ ref_for: true }, view.props, {
                        onRemove: ($event) => _unref(deleteView)(view.id)
                      }), null, 16, ["onRemove"]))
                    ]),
                    _: 2
                    /* DYNAMIC */
                  }, 1032, ["id", "index"]);
                }),
                128
                /* KEYED_FRAGMENT */
              ))
            ]),
            _: 1
            /* STABLE */
          }, 8, ["name", "on-before-enter", "on-enter", "on-after-enter", "on-before-leave", "on-leave", "on-after-leave", "onMouseenter", "onMouseleave"])
        ], 16, _hoisted_1)
      ], 8, ["to", "disabled"]);
    };
  }
});
</script>

<style>
@keyframes mt-slide-ttb-out {
  0% {
    transform: translate3d(0, 0, 0);
  }
  100% {
    transform: translate3d(0, calc(-100% - var(--magic-toast-padding-y)), 0);
  }
}

@keyframes mt-slide-ttb-in {
  0% {
    transform: translate3d(0, calc(-100% - var(--magic-toast-padding-y)), 0);
  }
  100% {
    transform: translate3d(0, 0, 0);
  }
}

@keyframes mt-slide-btt-out {
  0% {
    transform: translate3d(0, 0, 0);
  }
  100% {
    transform: translate3d(0, calc(100% + var(--magic-toast-padding-y)), 0);
  }
}

@keyframes mt-slide-btt-in {
  0% {
    transform: translate3d(0, calc(100% + var(--magic-toast-padding-y)), 0);
  }
  100% {
    transform: translate3d(0, 0, 0);
  }
}

:root {
  --magic-toast-padding-y: 1rem;
  --magic-toast-padding-x: 1rem;
  --magic-toast-gap: 0.75rem;
  --magic-toast-animation-duration: 175ms;
  --magic-toast-scale-factor: 0.05;
  --magic-toast-overlap-y: 1rem;

  --mt-multiplier-y: 0px;
  --mt-align-items: center;
  --mt-justify-content: flex-end;
  --mt-flex-direction: column;
  --mt-enter-animation: unset;
  --mt-leave-animation: unset;
}

.magic-toast-provider {
  position: var(--magic-toast-position, fixed);
  inset: var(--magic-toast-inset, 0);
  width: var(--magic-toast-width, 100%);
  height: var(--magic-toast-height, 100%);
  z-index: var(--magic-toast-z-index, 999);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}

.magic-toast-provider__list {
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 100%;
  width: 100%;
  height: 100%;
  display: flex;
  padding: var(--magic-toast-padding-y) var(--magic-toast-padding-x);
  flex-direction: var(--mt-flex-direction);
  align-items: var(--mt-align-items);
  justify-content: var(--mt-justify-content);
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.magic-toast-provider__list * {
  pointer-events: all;
}

.magic-toast-provider[data-position='top-left'],
.magic-toast-provider[data-position='top-center'],
.magic-toast-provider[data-position='top-right'] {
  --mt-enter-animation: mt-slide-ttb-in var(--magic-toast-animation-duration)
    var(--ease-in-out);
  --mt-leave-animation: mt-slide-ttb-out var(--magic-toast-animation-duration)
    var(--ease-in-out);
  --mt-multiplier-y: 1;
  --mt-justify-content: flex-end;
  --mt-flex-direction: column-reverse;
}

.magic-toast-provider[data-position='bottom-left'],
.magic-toast-provider[data-position='bottom-center'],
.magic-toast-provider[data-position='bottom-right'] {
  --mt-enter-animation: mt-slide-btt-in var(--magic-toast-animation-duration)
    var(--ease-in-out);
  --mt-leave-animation: mt-slide-btt-out var(--magic-toast-animation-duration)
    var(--ease-in-out);
  --mt-multiplier-y: -1;
  --mt-justify-content: flex-end;
}

.magic-toast-provider[data-position='top-left'],
.magic-toast-provider[data-position='bottom-left'] {
  --mt-align-items: flex-start;
}

.magic-toast-provider[data-position='top-right'],
.magic-toast-provider[data-position='bottom-right'] {
  --mt-align-items: flex-end;
}

.magic-toast-provider[data-position='center-left'] {
  --mt-justify-content: center;
  --mt-align-items: flex-start;
  --mt-enter-animation: slide-ltr-in var(--magic-toast-animation-duration)
    var(--ease-in-out);
  --mt-leave-animation: slide-ltr-out var(--magic-toast-animation-duration)
    var(--ease-in-out);
}

.magic-toast-provider[data-position='center-right'] {
  --mt-justify-content: center;
  --mt-align-items: flex-end;
  --mt-enter-animation: slide-rtl-in var(--magic-toast-animation-duration)
    var(--ease-in-out);
  --mt-leave-animation: slide-rtl-out var(--magic-toast-animation-duration)
    var(--ease-in-out);
}

.magic-toast-enter-active {
  animation: var(--mt-enter-animation, unset);
  position: absolute;

  &[data-position='bottom-left'],
  &[data-position='bottom-center'],
  &[data-position='bottom-right'] {
    bottom: calc(var(--mt-height) * var(--mt-index) * 1px);
  }

  &[data-position='top-left'],
  &[data-position='top-center'],
  &[data-position='top-right'] {
    top: calc(var(--mt-height) * var(--mt-index) * 1px);
  }
}

.magic-toast-leave-active {
  animation: fade-out var(--magic-toast-animation-duration) var(--ease-in-out);
  position: absolute;
}

.magic-toast-leave-active:last-child {
  animation: var(--mt-leave-animation, unset);
  position: absolute;
}

.magic-toast-move {
  transition: all var(--magic-toast-animation-duration) var(--ease-in-out);
}
</style>
